import { getOnStorage } from './storageFunctions';
import { buildScopedClient, parseBrandPairFromStorage } from './clientScope';
import { ClientProps } from '@/types';

/** Usado na composição da chave do carrinho em `cart_details` e de pedidos em `order_numbers`. */
export const DEFAULT_CART_BRAND_KEY = 'default_brand';

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Mesma regra que o CartContext: bandeira ativa vinda de `selected_brand` no storage.
 * Centralizado para checkout e limpeza de `order_numbers` alinharem ao carrinho escopado.
 */
export const getCartBrandKeyFromStorage = (): string => {
  const selectedBrand = getOnStorage('selected_brand');

  if (typeof selectedBrand === 'string') {
    return selectedBrand || DEFAULT_CART_BRAND_KEY;
  }

  if (selectedBrand && typeof selectedBrand === 'object') {
    const brandKey = (selectedBrand as { brandKey?: unknown }).brandKey;
    if (typeof brandKey === 'string' && brandKey) {
      return brandKey;
    }

    const codLoja = toNumber((selectedBrand as { codLoja?: number }).codLoja);
    const codSegmento = toNumber((selectedBrand as { codSegmento?: number }).codSegmento);
    if (codLoja != null && codSegmento != null) {
      return `${codLoja}:${codSegmento}`;
    }
  }

  return DEFAULT_CART_BRAND_KEY;
};

export const buildCartDetailsKey = (clientId?: number, brandKey?: string) => {
  if (!clientId) return '';
  return `${clientId}:${brandKey || DEFAULT_CART_BRAND_KEY}`;
};

/**
 * Condições de pagamento (`cod_condicao`) da loja ativa no `selected_brand` (multi-bandeira).
 * Usar em vez de `client.COD_PAGAMENTO` raiz ao validar `paymentCode` global.
 */
export const getScopedPaymentConditionCodes = (selectedClient: ClientProps | null | undefined): number[] => {
  if (!selectedClient) return [];
  const pair = parseBrandPairFromStorage(getOnStorage('selected_brand'));
  const scoped = buildScopedClient(selectedClient, pair);
  const pay = scoped?.COD_PAGAMENTO ?? selectedClient.COD_PAGAMENTO;
  return (Array.isArray(pay) ? pay : [])
    .map((i: { cod_condicao?: number; COD_CONDICAO?: number }) => Number(i?.cod_condicao ?? i?.COD_CONDICAO))
    .filter((n: number) => Number.isFinite(n) && n > 0);
};

export interface OrderNumberStorageEntry {
  orderNumber: string;
  lastUpdate: number;
}

/** Lê número de pedido: chave escopada primeiro; fallback legado só por `COD_CLIENTE`. */
export const getOrderNumberFromStorage = (
  clientId: number,
  brandKey: string,
  storedOrders: Record<string, OrderNumberStorageEntry | undefined>
): string | undefined => {
  const scoped = buildCartDetailsKey(clientId, brandKey);
  return storedOrders[scoped]?.orderNumber ?? storedOrders[String(clientId)]?.orderNumber;
};
